import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, getObsScore, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import ObsNumericStepper from './obsNumericStepper';
import {obsStyles} from '../obsStyle';

function ObsCRT(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [crt, setCrt] = React.useState(allowDefaultValue ? props.crt : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    function setDefaults() {
        setCrt(0);
    }

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setCrt(props.crt);
        }
    }, []);

    useEffect(() => {
        if ((_.isUndefined(crt) || _.isNull(crt)) && allowDefaultValue) {
            if (_.isUndefined(props.crt) || _.isNull(props.crt)) {
                setDefaults();
            } else {
                setCrt(props.crt);
            }
        }
    }, [props.crt]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        if (!allowDefaultValue) {
            setCrt(null);
        } else {
            setDefaults();
        }

        clearAccepted();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (!_.isNull(crt) && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [crt, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    crt,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [status, crt, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            if (type === obsTypes.pews && crt) {
                newScore = getObsScore('crt', type, ageRangeYears, crt);
            }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, crt]);

    const onCrtChange = (value) => {
        setCrt(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12}>
                        <ObsNumericStepper
                            value={crt}
                            minValue={0}
                            maxValue={100}
                            label={t('CRT')}
                            onChange={onCrtChange}
                        />
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(crt),
                        showStatusNR: type === obsTypes.pews,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsCRT.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    crt: PropTypes.number,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsCRT.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    crt: 0,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsCRT);